import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaUsers, FaStar } from "react-icons/fa";
import axios from 'axios';
import download from "downloadjs";
import Authentication from "../../api/Authentication/Authentication";
import API from "../../api/API";
import ContextMenu from "./ContextMenu";
import RenameModal from "./Modals/RenameModal";
import MoveItemModal from "./Modals/MoveItemModal";
import ShareFileModal from "./Modals/ShareFileModal";
import CreateFolderModal from "./Modals/CreateFolderModal";
import UserSelectModal from "./Modals/UserSelectModal";
import ToolBar from "./LeftPanel/ToolBar";
import FileGrid from "./LeftPanel/FileGrid";
import UploadZone from "./LeftPanel/UploadZone";
import RightPanel from "./RightPanel/RightPanel";
import Drawer from "./RightPanel/Drawer";
import TransferList from "./TransferList";
import "../../styles/controls/filebrowser_grid.css";

const FileBrowser = () => {
  // navigation
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract path after "/browser"
  const currentPath = decodeURIComponent(location.pathname.replace('/browser', '') || '/');

  const [directoryPathStack, setDirectoryPathStack] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Modals
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);  
  const [itemToMove, setItemToMove] = useState(null);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [currentDirectory, setCurrentDirectory] = useState(null);
  const [directoryData, setDirectoryData] = useState([]);
  const [lastClick, setLastClick] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);
  const [actionMenu, setActionMenu] = useState({item: null, selectionMode: false});
  
  // Selection lock
  var [lock, setLock] = useState(false); 
  const [selectedItems, setSelectedItems] = useState([]); // multi-selection state
  const [selectionMode, setSelectionMode] = useState(false);

  const [downloads, setDownloads] = useState([]); // List of downloads
  const [isDownloadListOpen, setIsDownloadListOpen] = useState(true);
  
  // Reference for file input (upload)
  const fileInputRef = useRef(null);

  // Active tab memo
  const activeTab = useMemo(() => {
    const firstSegment = currentPath.split('/').filter(Boolean)[0];
    if (firstSegment === 'shared') {
      return 'shared';
    } else if (firstSegment === 'favorites') {
      return 'favorites'
    } else {
      return 'personal'
    }
  }, [currentPath]);

  const tabs = [
    { id: 'favorites', label: 'Favorite', icon: <FaStar className="inline-block mr-2 text-yellow-500" /> },
    { id: 'personal', label: 'Cloud', icon: <FaHome className="inline-block mr-2" /> },
    { id: 'shared', label: 'Shared', icon: <FaUsers className="inline-block mr-2" /> },
  ];

  const isEditable = (fileName) => {
    const ext = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
  
    switch (ext) {
      case ".pdf":
        return true;
      case ".doc":
      case ".docx":
        return true;
      case ".xls":
      case ".xlsx":
        return true;
      case ".ppt":
      case ".pptx":
        return true;
      default:
        return false;
    }
  };
  
  /** Navigate into an item (Double Click) **/
  const navigateItems = (item) => {
    clearSelectedItems();
    if (item.type === 'dir') {      
      // navigation
      const base = activeTab === 'shared' 
                  ? '/browser/shared' 
                  : activeTab === 'favorites' 
                    ? '/browser/favorites' 
                    : '/browser';

      // Only strip the leading /shared if we're in the shared tab
      const cleanPath = activeTab === 'shared' || activeTab === 'favorites'
      ? currentPath.replace(/^\/(shared|favorites)(\/|$)/, '') 
      : currentPath.replace(/^\/+/, '');

      const newPath = cleanPath ? `${cleanPath}/${item.name}` : `${item.name}`;
      navigate(`${base}/${newPath}`);

      //getDirectoryInfo(item);
    } else {
      if (isEditable(item.name)) {
        handleFileEdit(item);
      } else {
        handleFileDownload(item);
      }
    }
  };

  /** Go back a directory level **/
  const navigateBack = () => {
    if (currentDirectory === "") return;
    if (currentPath === '/') return;

    // navigation
    const parts = currentPath.split('/').filter(Boolean);
    const newPath = '/' + parts.slice(0, -1).join('/');
    navigate(`/browser${newPath}`);

    // items
    clearSelectedItems();

    // path
    if (directoryPathStack.length > 1) {
      const newPathStack = [...directoryPathStack];
      newPathStack.pop();
      newPathStack.pop();
      setDirectoryPathStack(newPathStack);
    }
  };
  
  /**
  
  Selections

  **/ 
  // Toggle selection of an item.
  const toggleSelection = (item) => {
    setSelectedItems((prev) => {
      const exists = prev.find((sel) => sel.name === item.name);
      if (exists && prev.length === 1) {
        exitSelectionMode();
      }
      if (exists) {
        return prev.filter((sel) => sel.name !== item.name);
      } else {
        return [...prev, item];
      }
    });
  };

  // Clear selected items, context menu and action menu
  const clearSelectedItems = () => {
    setSelectedItems([]);
    setContextMenu(null);
    setActionMenu({item: null, selectionMode: false});
  }

  // Optionally, a function to exit selection mode.
  const exitSelectionMode = () => {
    setSelectionMode(false);
    clearSelectedItems();
  };
  
  // Close context menu
  const closeMenu = () => {
    setContextMenu(null);  
  };

  const handleDrawerOpen = (e) => {
    e.preventDefault(); 
    lock = true;
    setDrawerOpen(true);
    setLock(false);
  }

  /** Handle Single Click vs. Double Click **/
  const handleItemClick = (item) => {
    if (item) {
      lock = true;
      const now = Date.now();
      if (now - lastClick < 300 && !selectionMode) {
        navigateItems(item);
      } else {        
        if (!selectionMode) {
          // Enter selection mode if user intends to select items.
          setSelectedItems([item]);
        } else {
          // If in selection mode, toggle selection.
          toggleSelection(item);
        }
        setActionMenu({item, selectionMode});
      }
      setLastClick(now);
      setLock(false);
    }
  };
  
  /** Handle right-click in the container **/
  const handleContainerClick = (e) => {
    e.preventDefault();
    if (!lock) {
      exitSelectionMode();
    }
  };

  /** Handle Right-Click for Context Menu **/
  const handleItemContextClick = (e, item) => {
    e.preventDefault();
    if (item) {
      lock = true;
      if (!selectedItems.includes(item)) {
        handleItemClick(item);
      }
      setContextMenu({ x: e.clientX, y: e.clientY - 100, item, selectionMode });
      setLock(false);
    }
  };

  /** Handle right-click in the container **/
  const handleContainerContextClick = (e) => {
    e.preventDefault();
    if (!lock) {
      if (activeTab !== "personal") {
        exitSelectionMode();
        return;
      }
      exitSelectionMode();
      setContextMenu({ x: e.clientX, y: e.clientY - 100, item: false, selectionMode: false });
    }
  };

  /**
  
  MENU ACTIONS

  **/ 
  /** Handle Context Menu Actions **/
  const handleItemAction = (control, action) => {
    if (!control) return;
    const item = control.item;
    if (action === "open" && item.type === "dir") {
      navigateItems(item);
    } else if (action === "edit") {
      handleFileEdit(item);
    } else if (action === "download") {
      handleFileDownload(item);
    } else if (action === "create") {
      setShowCreateFolderModal(true);
    } else if (action === "upload") {
      fileInputRef.current.click()
    } else if (action === "select") {
      if (selectionMode) setSelectionMode(false)
      else setSelectionMode(true);
      setActionMenu({item, selectionMode: item? true : false});
      if (isMobile) setDrawerOpen(false);
    } else if (action === "copy") {
      handleCopy();
    } else if (action === "move") {
      setItemToMove(item);
      setMoveModalVisible(true);
    } else if (action === "rename") {
      setItemToRename(item);
      setShowRenameModal(true);
    } else if (action === "favorite") {
      handleFavoriteAction(item);
    } else if (action === 'sharewithuser') {
      setSelectedItem(item);
      setShowUserModal(true);
    } else if (action === 'share') {
      handleShareAction(item)    
    } else if (action === "delete") {
      handleDelete();
    }
    setContextMenu(null);
  };

  /**
  
  MENU ACTIONS

  **/
  const handleFileEdit = async (item) => {
    const currentUser = Authentication.getCurrentUser() 
    const editorData = {
      userID: currentUser._id,
      token: currentUser.token,
      userName: currentUser.username,
      itemId: item.id
    };

    const params = new URLSearchParams(editorData).toString();
    const url = `/editor?${params}`;
    window.open(url, '_blank');
    
    getDirectoryInfo(currentDirectory, true);
    setDrawerOpen(false);
  }

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    
    if (files.length < 1) return;

    for (const file of files) {
      // Create a cancel token for this file upload
      const cancelTokenSource = axios.CancelToken.source();

      // Create a new transfer entry for the upload with a unique id and type "upload"
      const newTransfer = { 
        id: Date.now(), 
        name: file.name, 
        progress: 0, 
        size: file.size, 
        completed: false, 
        type: "upload",
        cancelTokenSource, 
      };
      setDownloads((prev) => [...prev, newTransfer]);

      try {
        const res = await API.uploadFile(            
          Authentication.getCurrentUser()?._id, 
          Authentication.getCurrentUser()?.token, 
          currentDirectory.id,
          file.name,
          file, 
          {
            cancelToken: cancelTokenSource.token,
            onUploadProgress: (progressEvent) => {
              // Update the progress of the current upload
              setDownloads((prev) =>
                prev.map((transfer) =>
                  transfer.id === newTransfer.id
                    ? { ...transfer, progress: (progressEvent.loaded / file.size) * 100 }
                    : transfer
                )
              );
            },
          }
        );
        
        if (res) {
          if (res.data.success) {
            // Mark the upload as completed
            setDownloads((prev) =>
              prev.map((transfer) =>
                transfer.id === newTransfer.id
                  ? { ...transfer, progress: 100, completed: true }
                  : transfer
              )
            );
              
            // Refresh the directory listing if needed
            getDirectoryInfo(currentDirectory, true);
            setDrawerOpen(false);
            exitSelectionMode();
          } else {          
            console.error(`Upload problem for ${file.name}:`, res.info);
            // Optionally, remove the transfer or mark it as failed
            setDownloads((prev) =>
              prev.map((transfer) =>
                transfer.id === newTransfer.id
                  ? { ...transfer, progress: 100, failed: true }
                  : transfer
              )
            );
          }
        }

      } catch(error) {
          if (axios.isCancel(error)) {
            console.log(`Upload canceled for ${file.name}`);
            // Optionally, remove the transfer or mark it as failed
            setDownloads((prev) =>
              prev.map((transfer) =>
                transfer.id === newTransfer.id
                  ? { ...transfer, progress: 100, aborted: true,  }
                  : transfer
              )
            );
          } else {
            console.error(`Upload error for ${file.name}:`, error);
            // Optionally, remove the transfer or mark it as failed
            setDownloads((prev) =>
              prev.map((transfer) =>
                transfer.id === newTransfer.id
                  ? { ...transfer, progress: 100, failed: true }
                  : transfer
              )
            );
          }  
      };
    }
  }

  const handleFileUploadComplete = async (newTransfer) => {
    // Mark the upload as completed
    setDownloads((prev) =>
      prev.map((transfer) =>
        transfer.id === newTransfer.id
          ? { ...transfer, progress: 100, completed: true }
          : transfer
      )
    );
    getDirectoryInfo(currentDirectory, true);
    setDrawerOpen(false);
  }

  const handleFileDownload = async (item) => {
    // Create an Axios CancelToken for this download
    const cancelTokenSource = axios.CancelToken.source();

    // Create a new transfer entry for the upload with a unique id and type "upload"
    const newTransfer = { 
      id: Date.now(), 
      name: item.name, 
      progress: 0, 
      size: item.size, 
      completed: false, 
      type: "download",
      cancelTokenSource, 
    };
    // Add file to downloads list with a unique id (using timestamp)
    setDownloads((prev) => [...prev, newTransfer]);
    try {
      const res = await API.downloadFile(
        Authentication.getCurrentUser()?._id, 
        Authentication.getCurrentUser()?.token,
        item.id,
        newTransfer.name,
        newTransfer.id,
        (event) => updateProgressBar(event, newTransfer.id),
        cancelTokenSource.token,
      )
      
      if (res) {
        if (res.status === 200) {
          const blob = new Blob([res.data], { type: res.headers["content-type"] });
          download(blob, item.name, res.headers["content-type"]);
              
          // Mark the upload as completed
          setDownloads((prev) =>
            prev.map((transfer) =>
              transfer.id === newTransfer.id
                ? { ...transfer, progress: 100, completed: true }
                : transfer
            )
          );
        } else {          
          console.error(`Download problem for ${item.name}:`, res);
          // Optionally, remove the transfer or mark it as failed
          setDownloads((prev) =>
            prev.map((transfer) =>
              transfer.id === newTransfer.id
                ? { ...transfer, progress: 100, failed: true }
                : transfer
            )
          );
        }
      }
    } catch(error) {
      if (axios.isCancel(error)) {
        console.log(`Download canceled for ${item.name}`);
        // Optionally, remove the transfer or mark it as failed
        setDownloads((prev) =>
          prev.map((transfer) =>
            transfer.id === newTransfer.id
              ? { ...transfer, progress: 100, aborted: true,  }
              : transfer
          )
        );
      } else {
        console.error(`Download error for ${item.name}:`, error);
          // Optionally, remove the transfer or mark it as failed
        setDownloads((prev) =>
          prev.map((transfer) =>
            transfer.id === newTransfer.id
              ? { ...transfer, progress: 100, failed: true }
              : transfer
          )
        );
      }
    }
  }

  const updateProgressBar = useCallback((event, id) => {
    setDownloads((prev) =>
      prev.map((file) =>
        file.id === id
          ? { ...file, progress: (event.loaded / file.size) * 100 }
          : file
      )
    );
  }, []);

  const handleCreateFolderSave = async (folderName) => {
    try {
      const result = await API.createItem(
        Authentication.getCurrentUser()?._id, 
        Authentication.getCurrentUser()?.token, 
        currentDirectory.id, 
        encodeURI(folderName)
      );
      if (result.data.success) {
        // Refresh directory listing
        getDirectoryInfo(currentDirectory, true);
        setDrawerOpen(false);
      } else {
        alert("Folder creation failed: " + result.data.info);
      }
      exitSelectionMode();
    } catch (error) {
      alert("An error occurred during folder creation.");
    } finally {
      setShowCreateFolderModal(false);
    }
  };
  
  const handleCreateFolderCancel = () => {
    setShowCreateFolderModal(false);
  };

  const handleCopy = async () => {
    selectedItems.forEach(async (item) => {
      if (item) {
        //const newFolderName = prompt("Enter new folder name for the copy:");
        try {
          const result = await API.copyItem(
            Authentication.getCurrentUser()?._id, 
            Authentication.getCurrentUser()?.token, 
            item.id, 
            currentDirectory.id
          );
          if (result.data.success) {
            // Refresh the directory listing
            getDirectoryInfo(currentDirectory, true);
            setDrawerOpen(false);
          } else {
            alert("Failed to copy item: " + result.data.info);
          }
          exitSelectionMode();
        } catch (error) {
          console.error("Error copying item:", error);
          alert("An error occurred during copying.");
        }
      }  
    });
  };

  const handleMoveSave = async (destination) => {
    try {
      const result = await API.moveItem(
        Authentication.getCurrentUser()?._id, 
        Authentication.getCurrentUser()?.token,
        itemToMove.id,
        destination.id,
      );
      if (result.data.success) {
        getDirectoryInfo(currentDirectory, true);
        setDrawerOpen(false);
      } else {
        alert("Move failed: " + result.data.info);
      }
      exitSelectionMode();
    } catch (error) {
      console.error("Error moving item:", error);
      alert("An error occurred during the move.");
    } finally {
      setMoveModalVisible(false);
      setItemToMove(null);
    }
  };

  const handleMoveCancel = () => {
    setMoveModalVisible(false);
    setItemToMove(null);
  };

  const handleRenameSave = async (newName) => {
    if (itemToRename) {
      try {
        const result = await API.renameFile(
          Authentication.getCurrentUser()?._id, 
          Authentication.getCurrentUser()?.token,
          itemToRename.id,
          encodeURI(newName)
        );
        if (result.data.success) {
          // Refresh directory listing
          getDirectoryInfo(currentDirectory, true);
          setDrawerOpen(false);
        } else {
          alert("Rename failed: " + result.data.info);
        }      
        exitSelectionMode();
      } catch (error) {
        console.error("Rename error:", error);
        alert("An error occurred during renaming.");
      }
    }
    setShowRenameModal(false);
    setItemToRename(null);
  };

  const handleRenameCancel = () => {
    setShowRenameModal(false);
    setItemToRename(null);
  };
  
  const handleFavoriteAction = async (item) => {
    const currentUser = Authentication.getCurrentUser();
    if (!currentUser) return;
  
    try {
      const response = await API.setFavorites(currentUser._id, currentUser.token, item.id, !item.favorite);
  
      if (response.data.success) {
        getDirectoryInfo(currentDirectory, true);
        setDrawerOpen(false);
        exitSelectionMode();
      } else {
        console.warn("Favorite toggle failed:", response.data.info);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const handleShareAction = async (item) => {
    // Compute the share link from currentDirectory and item name.
    // This assumes your backend serves files via a public URL, e.g., /files
    const result = await API.createShare(      
      Authentication.getCurrentUser()?._id, 
      Authentication.getCurrentUser()?.token,
      item.id,
      //24 * 60 * 60 * 1000 // 24 hours expiration
    );
    if (result.data.success) {
      const link = encodeURI(result.data.info.shareLink);
      setShareLink(link);
      setShowShareModal(true);
    }
  };

  const handleShareWithUserAction  = async (selectedUserIds) => {
    try {
      await API.shareItemWithUsers(
        Authentication.getCurrentUser()?._id,
        Authentication.getCurrentUser()?.token,
        selectedItem.id,
        selectedUserIds
      );
      // Refresh directory listing
      getDirectoryInfo(currentDirectory, true);
      setDrawerOpen(false);
    } catch (err) {
      console.error("Error sharing:", err.response?.data || err.message);
    }
    setShowUserModal(false);
    setSelectedItem(null);
  };

  const handleDelete = async () => {
    if (selectedItems.length > 1) {
      if (!window.confirm(`Are you sure you want to delete the ${selectedItems.length} selected items?`)) return;
    } else if (selectedItems.length === 1) {
      if (!window.confirm(`Are you sure you want to delete "${selectedItems[0].name}"?`)) return;
    } else {      
      alert("Please select item to delete!");
    }
    selectedItems.forEach(async (item) => {
      if (item) {
        try {
          const result = await API.deleteFile(
            Authentication.getCurrentUser()?._id, 
            Authentication.getCurrentUser()?.token,
            item.id 
          );
          if (result.data.success) {
            getDirectoryInfo(currentDirectory, true);
            setDrawerOpen(false);
          } else {
            alert("Delete failed.");
          }
          exitSelectionMode();
        } catch (error) {
          console.error("Delete error:", error);
          alert("An error occurred during deletion.");
        }
      }
    });
  };
  
  /**

  TRANSFER LIST

  **/
  const removeTransfer = (transferId) => {
    setDownloads((prev) => prev.filter((t) => t.id !== transferId));
  };

  const clearTransfers = () => {
    setDownloads((prev) =>
      prev.filter((transfer) =>
        (transfer.completed !== true && transfer.aborted !== true && transfer.failed !== true)
      )
    );
  };

  /**

  DIR STUFF

  **/
  /** Fetch Directory or Download File **/
  const getDirectoryInfo = useCallback(
    (item, reload = false) => {
      if (activeTab === 'personal') {
        API.getDirecotryInfo(
          Authentication.getCurrentUser()?._id, 
          Authentication.getCurrentUser()?.token, 
          item.id
        ).then((result) => {
          if (result.data.success) {
            setDirectoryData(result.data.info.children);
            setCurrentDirectory(result.data.info.parent);

            if (reload === false) {
              // const parent = result.data.info.parent;            
              // /// If root, reset the path stack
              // if (!parent?.name === '/') {
              //   setDirectoryPathStack([{ name: parent.name, id: parent.id }]);
              // } else {
              //   if (parent.name === '/') {
              //     parent.name = 'Main Cloud';
              //   }
              //   setDirectoryPathStack(prev => [...prev, { name: parent.name, id: parent.id }]);
              // }
              
              const parts = currentPath.split('/').filter(Boolean);
              setDirectoryPathStack([{name: 'Main Cloud', id: null}]);
              parts.forEach(part => {                
                setDirectoryPathStack(prev => [...prev, { name: part, id: null }]);
              });

            }
          }
        });
      } else if (activeTab === 'shared') {        
        API.getSharedItems(
          Authentication.getCurrentUser()?._id, 
          Authentication.getCurrentUser()?.token, 
          item.id) // For root shared
        .then((result) => {
          setDirectoryData(result.data.info.children);
          setCurrentDirectory(result.data.info.parent);

          if (reload === false) {
            // const parent = result.data.info.parent;
            // /// If root, reset the path stack
            // if (!parent?.name === '/') {
            //   setDirectoryPathStack([{ name: parent.name, id: parent.id }]);
            // } else {
            //   setDirectoryPathStack(prev => [...prev, { name: parent.name, id: parent.id }]);
            // }

            const cleanPath = currentPath.replace(/^\/(shared)?/, '').replace(/^\/+/, ''); // remove "shared" and any leading slashes
            const parts = cleanPath.split('/').filter(Boolean);
            setDirectoryPathStack([{name: 'Shared', id: null}]);
            parts.forEach(part => {                
              setDirectoryPathStack(prev => [...prev, { name: part, id: null }]);
            });
          }
        })
        .catch((err) => {
          console.error("Error loading shared items:", err.response?.data || err.message);
        });
      } else if (activeTab === 'favorites') {
        API.getFavorites(
          Authentication.getCurrentUser()?._id,
          Authentication.getCurrentUser()?.token,
          item.id
        )
        .then((result) => {
          setDirectoryData(result.data.info.children);
          setCurrentDirectory(result.data.info.parent);
          
          if (!reload) {

            const cleanPath = currentPath.replace(/^\/(favorites)?/, '').replace(/^\/+/, '');
            const parts = cleanPath.split('/').filter(Boolean);
            setDirectoryPathStack([{ name: 'Favorites', id: null }]);
            parts.forEach(part => {
              setDirectoryPathStack(prev => [...prev, { name: part, id: null }]);
            });
          }
        })
        .catch((err) => {
          console.error("Error loading favorite items:", err.response?.data || err.message);
        });
      }
    },
    [currentPath, activeTab]
  );

  const handleActiveTabChange = (id) => {
    if (activeTab !== id) {      
      if (id === 'personal') {
        navigate('/browser');
      } else if (id === 'shared') {
        navigate('/browser/shared');
      } else if (id === 'favorites') {
        navigate('/browser/favorites');
      }
    }
  }

  // Memoized sorted directory data: folders first, then files; both sorted by name.
  const sortedDirectoryData = useMemo(() => {
    return [...directoryData].sort((a, b) => {
      // If both items are of the same type, sort alphabetically by name.
      if (a.type === b.type) {
        return a.name.localeCompare(b.name);
      }
      // Otherwise, folders ("dir") should come first.
      return a.type === "dir" ? -1 : 1;
    });
  }, [directoryData]);

  /**

  USE EFFECTS

  **/  
  useEffect(() => {
    const currentUser = Authentication.getCurrentUser();
    if (!currentUser) return;
  
    const cleanPath = currentPath.replace(/^\/(shared|favorites)?/, '').replace(/^\/+/, ''); // remove shared/favorites
    const parts = cleanPath.split('/').filter(Boolean);
  
    if (parts.length === 0) {
      // Load root
      getDirectoryInfo({ id: null });
      return;
    }

    if (activeTab === 'personal') {
      API.resolveFolderPath(currentUser._id, currentUser.token, '/' + parts.join('/'))
        .then(res => {
          if (res.data.success) {
            getDirectoryInfo({ id: res.data.info.parent.id });
          } else {
            console.warn("Path resolution failed:", res.data.info);
            getDirectoryInfo({ id: null });
          }
        })
        .catch(err => {
          console.error("Error resolving path:", err);
          getDirectoryInfo({ id: null });
        });
    } else if (activeTab === 'shared') {
      const sharedPath = '/' + parts.join('/');
      API.resolveSharedPath(currentUser._id, currentUser.token, sharedPath)
        .then(res => {
          if (res.data.success) {
            getDirectoryInfo({ id: res.data.info.itemId });
          } else {
            console.warn("Shared path resolution failed:", res.data.info);
            getDirectoryInfo({ id: null });
          }
        })
        .catch(err => {
          console.error("Error resolving shared path:", err);
          getDirectoryInfo({ id: null });
        });
    } else if (activeTab === 'favorites') {
      const favoritesPath = '/' + parts.join('/');
      API.resolveFavoritesPath(currentUser._id, currentUser.token, favoritesPath)
        .then(res => {
          if (res.data.success) {
            getDirectoryInfo({ id: res.data.info.itemId });
          } else {
            console.warn("Favorites path resolution failed:", res.data.info);
            getDirectoryInfo({ id: null });
          }
        })
        .catch(err => {
          console.error("Error resolving favorites path:", err);
          getDirectoryInfo({ id: null });
        });
    }
  }, [activeTab, currentPath, getDirectoryInfo]);  
  
  // dismiss context menu
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // If the context menu is open, close it
      if (contextMenu) {
        setContextMenu(null);
      }
    };
  
    window.addEventListener("click", handleGlobalClick);
    
    return () => {
      window.removeEventListener("click", handleGlobalClick);
    };
  }, [contextMenu]);
  
  // Global drag event listeners to show/hide the upload zone
  useEffect(() => {
    const handleDragEnter = (e) => {
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };
    const handleDragLeave = (e) => {
      if (e.relatedTarget == null) {
        setIsDragging(false);
      }
    };
    const handleDrop = (e) => {
      setIsDragging(false);
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);
  
  // Set whether the screen is mobile sized or not
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    
    // Run the function once after mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="bg-gray-100 dark:bg-gray-900 p-2 flex max-h-[100dvh] min-h-[100dvh]">
      {/* Left Section: File Browser */}
      <div
        className={`max-w-8xl bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg ${isMobile? 'w-full':'w-3/4'}`}
        onClick={(e) => handleContainerClick(e)}
        onContextMenu={(e) => handleContainerContextClick(e)}
      >
        {/* Tab Header */}
        <div className="border-b border-gray-700 bg-gray-100 dark:bg-gray-900 rounded-md">
          <div
            className={`grid w-full text-center ${
              tabs.length === 2 ? "grid-cols-2" :
              tabs.length === 3 ? "grid-cols-3" :
              tabs.length === 4 ? "grid-cols-4" : "grid-cols-1"
            }`}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`px-4 py-2 rounded-md transition-all flex justify-center items-center gap-2
                  ${activeTab === tab.id
                    ? "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 border-b-2 border-blue-500 font-semibold shadow-sm"
                    : "text-gray-800 dark:text-gray-200 opacity-60 hover:opacity-100 border-b-2 border-gray-500"
                  }`}
                onClick={() => handleActiveTabChange(tab.id)}
              >
                <span className="flex items-center justify-center text-lg">{tab.icon}</span>
                <span className="text-sm">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      
        {/* File upload */}    
        {isDragging && (
          <UploadZone
            currentDirectory={currentDirectory}
            onUploadComplete={handleFileUploadComplete}
            setDownloads={setDownloads}
          />
        )}     

        {/* Tool Bar */}        
        <div>
          <ToolBar
            currentDirectory={directoryPathStack}
            isMobile={isMobile}
            handleDrawerOpen={handleDrawerOpen}
            navigateBack={navigateBack}
            activeTab={activeTab}
          />
        </div>

        {/* File Grid */}        
        <div>
          <FileGrid
            directoryData={sortedDirectoryData}
            onClick={handleItemClick}
            onContextMenu={handleItemContextClick}
            selectedItems={selectedItems}
            selectionMode={selectionMode}
            toggleSelection={toggleSelection}
            activeTab={activeTab}
            onToggleFavorite={handleFavoriteAction}
          />
        </div>

      </div>

      {/* Right Section: Details Panel */}
      {isMobile ? (
          <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)}>
            <RightPanel 
              activeTab={activeTab}
              selectedItems={selectedItems}
              selectionMode={selectionMode}
              currentDirectory={currentDirectory}
              isMobile={isMobile}
              actionMenu={actionMenu}
              exitSelectionMode={exitSelectionMode}
              handleItemAction={handleItemAction}
            />
          </Drawer>
        ) : (
          <RightPanel 
            activeTab={activeTab}
            selectedItems={selectedItems}
            selectionMode={selectionMode}
            currentDirectory={currentDirectory}
            isMobile={isMobile}
            actionMenu={actionMenu}
            exitSelectionMode={exitSelectionMode}
            handleItemAction={handleItemAction}
          />
        )
      }

      {/* Create folder box */}
      {showCreateFolderModal && (
        <CreateFolderModal
          initialName="New Folder"
          onSave={handleCreateFolderSave}
          onCancel={handleCreateFolderCancel}
        />
      )}

      {/* Move item box */}
      {moveModalVisible && (
        <MoveItemModal
          startDir={currentDirectory}
          excludeFolder={itemToMove}
          onSave={handleMoveSave}
          onCancel={handleMoveCancel}
        />
      )}

      {/* Rename item box */}
      {showRenameModal && (
        <RenameModal
          initialName={itemToRename.name}
          onSave={handleRenameSave}
          onCancel={handleRenameCancel}
        />
      )}
    
      {/* Share file box */}
      {showShareModal && (
        <ShareFileModal
          shareLink={shareLink}
          onCancel={() => setShowShareModal(false)}
        />
      )}
      
      {/* Share file box */}
      {showUserModal && selectedItem && (
        <UserSelectModal
          item={selectedItem}
          onConfirm={handleShareWithUserAction}
          onCancel={() => setShowUserModal(false)}
        />
      )}
      {/* Context Menu */}
      <ContextMenu contextMenu={contextMenu} handleAction={handleItemAction} closeMenu={closeMenu} activeTab={activeTab} />

      {/* Download List */}
      <TransferList
        transfers={downloads} 
        isOpen={isDownloadListOpen}
        toggleOpen={() => setIsDownloadListOpen((prev) => !prev)}        
        clearTransfers={clearTransfers}
        removeTransfer={removeTransfer}
      />

      <input type="file" ref={fileInputRef} className="hidden" onChange={(e) => handleFileUpload(e)} multiple />

    </div>
  );
};

export default FileBrowser;